<template>
  <Teleport to="body">
    <Transition name="demo-dialog">
      <div
        v-if="isOpen"
        ref="dialogLayer"
        class="demo-dialog-layer"
        role="presentation"
        @mousedown.self="closeDialog"
      >
        <section
          ref="dialogPanel"
          class="demo-dialog-panel"
          :class="{ 'demo-dialog-panel-booking': showsCalendar || submitted }"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-dialog-title"
          @mousedown.stop
        >
          <button
            type="button"
            class="demo-dialog-close"
            aria-label="Close demo request"
            @click="closeDialog"
          >
            <span aria-hidden="true">&times;</span>
          </button>

          <div class="demo-dialog-main">
            <div class="demo-dialog-progress" role="group" aria-label="Form progress">
              <div class="demo-dialog-progress-copy">
                <span>{{ flow === 'calendar' ? 'Book a demo' : 'Demo request' }}</span>
                <strong>Step {{ Math.min(progressIndex + 1, totalSteps) }} of {{ totalSteps }}</strong>
              </div>
              <div class="demo-dialog-progress-track" aria-hidden="true">
                <span
                  v-for="index in totalSteps"
                  :key="index"
                  :class="{ complete: index <= progressIndex + 1 }"
                ></span>
              </div>
            </div>

            <!-- Calendar first (booking.js reports the time hand-off): the
                 369.dev calendar picks the time and hands it back as w369:slot;
                 nothing is booked or posted yet. -->
            <div
              v-if="flow === 'calendar' && calendarStage === 'time'"
              class="demo-dialog-step demo-dialog-booking-step"
            >
              <h2 id="demo-dialog-title" tabindex="-1">Pick a time for your demo</h2>
              <!-- What the request involves, before the first choice: no surprise steps. -->
              <p class="demo-dialog-booking-lede">A 30-minute video call. Times are in your time zone; you'll add your details next.</p>
              <div id="demo-booking-slot" ref="bookingSlot" class="demo-dialog-booking-slot" data-booking-slot></div>
              <p v-if="bookingUnavailable" class="demo-dialog-booking-fallback">
                Trouble loading the calendar? Write to
                <a href="mailto:support@workspace369.com?subject=Workspace369%20demo%20request">support@workspace369.com</a>
                and we will lock in a time.
              </p>
              <button type="button" class="demo-dialog-request-only" @click="startRequestOnly">
                None of these times work? Send a request instead
              </button>
            </div>

            <fieldset v-else-if="flow !== 'calendar' && currentStep" :key="currentStep.id" class="demo-dialog-step">
              <legend id="demo-dialog-title" tabindex="-1">{{ currentStep.question }}</legend>
              <p v-if="currentStep.help">{{ currentStep.help }}</p>
              <!-- What the request involves, before the first answer: no surprise steps. -->
              <p v-if="stageIndex === 0" class="demo-dialog-terms">
                <template v-if="flow === 'request'">A 30-minute video call. {{ steps.length }} quick questions and your contact details, then we email you to find a time.</template>
                <template v-else>A 30-minute video call. {{ steps.length }} quick questions and your contact details, then you pick a time.</template>
              </p>

              <div class="demo-dialog-options">
                <button
                  v-for="option in currentStep.options"
                  :key="option.value"
                  class="demo-dialog-option"
                  :class="{ selected: answers[currentStep.id] === option.value }"
                  :aria-pressed="answers[currentStep.id] === option.value"
                  type="button"
                  @click="selectOption(currentStep.id, option.value)"
                >
                  <span class="demo-dialog-option-copy">
                    <strong>{{ option.label }}</strong>
                    <small v-if="option.description">{{ option.description }}</small>
                  </span>
                  <span class="demo-dialog-option-arrow" aria-hidden="true">→</span>
                </button>
              </div>
            </fieldset>

            <fieldset
              v-else-if="flow === 'calendar' ? calendarStage === 'details' : !submitted"
              class="demo-dialog-step demo-dialog-contact-step"
            >
              <div v-if="flow === 'calendar' && chosenSlot" class="demo-dialog-slot-chip">
                <strong>{{ chosenSlotLabel }}</strong>
                <button type="button" @click="changeTime">Change</button>
              </div>
              <legend id="demo-dialog-title" tabindex="-1">{{ flow === 'calendar' ? 'Your details' : 'Where should we follow up?' }}</legend>
              <p>Just the essentials. We use these details only to respond to this request.</p>

              <div class="demo-dialog-fields">
                <div class="demo-dialog-name-row">
                  <label>
                    <span>First name</span>
                    <input
                      v-model.trim="contact.firstName"
                      autocomplete="given-name"
                      name="firstName"
                      required
                      type="text"
                      :aria-invalid="Boolean(fieldErrors.firstName)"
                      :aria-describedby="fieldErrors.firstName ? 'demo-error-firstName' : undefined"
                      @input="fieldErrors.firstName = ''"
                    />
                    <p v-if="fieldErrors.firstName" id="demo-error-firstName" class="demo-dialog-field-error" role="alert">
                      {{ fieldErrors.firstName }}
                    </p>
                  </label>
                  <label>
                    <span>Last name</span>
                    <input
                      v-model.trim="contact.lastName"
                      autocomplete="family-name"
                      name="lastName"
                      required
                      type="text"
                      :aria-invalid="Boolean(fieldErrors.lastName)"
                      :aria-describedby="fieldErrors.lastName ? 'demo-error-lastName' : undefined"
                      @input="fieldErrors.lastName = ''"
                    />
                    <p v-if="fieldErrors.lastName" id="demo-error-lastName" class="demo-dialog-field-error" role="alert">
                      {{ fieldErrors.lastName }}
                    </p>
                  </label>
                </div>
                <label>
                  <span>Work email</span>
                  <input
                    v-model.trim="contact.email"
                    autocomplete="email"
                    inputmode="email"
                    name="email"
                    required
                    type="email"
                    :aria-invalid="Boolean(fieldErrors.email)"
                    :aria-describedby="fieldErrors.email ? 'demo-error-email' : undefined"
                    @input="fieldErrors.email = ''"
                  />
                  <p v-if="fieldErrors.email" id="demo-error-email" class="demo-dialog-field-error" role="alert">
                    {{ fieldErrors.email }}
                  </p>
                </label>
                <!-- Not a wrapping <label>: it would forward clicks landing on the
                     country popover to the first control inside it. -->
                <div class="demo-dialog-field">
                  <label for="demo-phone-input">
                    <span>Phone number <em>Optional</em></span>
                  </label>
                  <div class="demo-dialog-phone-row">
                    <PhoneCountrySelect v-model="countryIso" />
                    <input
                      id="demo-phone-input"
                      v-model.trim="contact.phone"
                      autocomplete="tel-national"
                      inputmode="tel"
                      name="phone"
                      placeholder="415 555 0100"
                      type="tel"
                      :aria-invalid="Boolean(fieldErrors.phone)"
                      :aria-describedby="fieldErrors.phone ? 'demo-error-phone' : undefined"
                      @input="fieldErrors.phone = ''"
                    />
                  </div>
                  <p v-if="fieldErrors.phone" id="demo-error-phone" class="demo-dialog-field-error" role="alert">
                    {{ fieldErrors.phone }}
                  </p>
                </div>
                <label>
                  <span>Anything else we should know? <em>Optional</em></span>
                  <textarea v-model.trim="contact.message" name="message" rows="3"></textarea>
                </label>
              </div>

              <!-- The four intake questions, optional here: the time already
                   says the visitor wants a call, and the answers still ride on
                   the lead and the booking when given. -->
              <div v-if="flow === 'calendar'" class="demo-dialog-fields demo-dialog-prepare">
                <p class="demo-dialog-prepare-title">Help us prepare <em>Optional</em></p>
                <div class="demo-dialog-prepare-grid">
                  <label v-for="step in steps" :key="step.id">
                    <span>{{ step.question }}</span>
                    <select v-model="answers[step.id]" :name="step.id">
                      <option value="">Choose…</option>
                      <option v-for="option in step.options" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>
                  </label>
                </div>
              </div>

              <div class="demo-dialog-honeypot" aria-hidden="true">
                <label for="demo-company-website">Company website</label>
                <input
                  id="demo-company-website"
                  v-model="contact.botField"
                  name="company_website"
                  type="text"
                  tabindex="-1"
                  autocomplete="off"
                />
              </div>

              <p class="demo-dialog-privacy">
                By submitting, you agree that Workspace369 may contact you about this request.
                See our
                <a href="/privacy-policy">Privacy Policy</a>.
              </p>
            </fieldset>

            <!-- Request only ("None of these times work"): the lead is in; no calendar follows. -->
            <div v-else-if="flow === 'request'" class="demo-dialog-step demo-dialog-booking-step" role="status">
              <h2 id="demo-dialog-title" tabindex="-1">Thanks, your request is in</h2>
              <p class="demo-dialog-booking-lede">We'll email you at {{ contact.email }} to find a time that works.</p>
              <button type="button" class="demo-dialog-done" @click="closeDialog">Done</button>
            </div>

            <div v-else class="demo-dialog-step demo-dialog-booking-step">
              <h2 id="demo-dialog-title" tabindex="-1">{{ flow === 'calendar' ? 'Confirm your demo' : 'Pick time and date' }}</h2>
              <!-- The lead is already saved at this point; closing without a time does not lose it. -->
              <p class="demo-dialog-booking-note" role="status">
                <template v-if="flow === 'calendar'">Your details are in. Confirm the time below, or close this and we will get back to you at {{ contact.email }}.</template>
                <template v-else>Your request is in. Pick a time below, or close this and we will get back to you at {{ contact.email }}.</template>
              </p>

              <!-- In-app booking calendar: mounted here via window.W369Booking.mount()
                   (booking.js is loaded site-wide in api mode from nuxt.config). -->
              <div id="demo-booking-slot" ref="bookingSlot" class="demo-dialog-booking-slot" data-booking-slot></div>

              <!-- Shown only if the scheduler never rendered: every demo CTA on the
                   site funnels here, so a silent failure must not be a dead end. -->
              <p v-if="bookingUnavailable" class="demo-dialog-booking-fallback">
                Trouble loading the calendar? Write to
                <a href="mailto:support@workspace369.com?subject=Workspace369%20demo%20request">support@workspace369.com</a>
                and we will lock in a time.
              </p>

              <button type="button" class="demo-dialog-done" @click="closeDialog">Done</button>
            </div>

            <div v-if="showsControls" class="demo-dialog-controls">
              <button
                v-if="flow === 'calendar' || stageIndex > 0"
                class="demo-dialog-back"
                type="button"
                @click="goBack"
              >
                <span aria-hidden="true">←</span> Back
              </button>
              <span v-else></span>

              <button
                v-if="flow === 'calendar' || !currentStep"
                class="demo-dialog-submit"
                :disabled="submitting"
                type="button"
                @click="submitForm"
              >
                {{ submitting ? 'Sending…' : flow === 'calendar' ? 'Continue' : 'Request demo' }}
                <span v-if="!submitting" aria-hidden="true">→</span>
              </button>
            </div>

            <p v-if="submitError" class="demo-dialog-error" role="alert">
              {{ submitError }}
            </p>
          </div>

          <aside class="demo-dialog-proof" aria-label="What customers say">
            <svg class="demo-dialog-proof-quote" viewBox="0 0 48 36" aria-hidden="true">
              <path d="M0 36V22.6C0 9.4 6.9 2 18.4 0l2.2 6.6c-6.5 1.8-9.9 5.6-10.3 11.4H18V36H0Zm27.6 0V22.6C27.6 9.4 34.5 2 46 0l2 6.6c-6.5 1.8-9.8 5.6-10.2 11.4h7.6V36H27.6Z" fill="currentColor" />
            </svg>
            <p class="demo-dialog-proof-kicker">Selected App Store reviews</p>
            <h3>Built for teams that run on client work.</h3>
            <ul>
              <li v-for="review in proofReviews" :key="review.reviewId">
                <span class="demo-dialog-stars" aria-hidden="true">{{ '★'.repeat(review.stars) }}{{ '☆'.repeat(5 - review.stars) }}</span>
                <blockquote translate="no" lang="en">“{{ review.quote }}”</blockquote>
                <p>
                  <span translate="no">{{ review.author }}</span> ·
                  <time translate="no" :datetime="review.date">{{ review.date }}</time> ·
                  <a :href="review.sourceUrl" target="_blank" rel="noopener">App Store review</a>
                </p>
              </li>
            </ul>
            <p class="demo-dialog-proof-foot">
              Quoted exactly as written; a selection, not all reviews. The overall rating includes ratings from our team.
            </p>
            <p class="demo-dialog-proof-foot demo-dialog-proof-foot-trial">
              Your answers shape the call; no generic pitch.
            </p>
            <svg class="demo-dialog-proof-ridge" viewBox="0 0 340 90" preserveAspectRatio="none" aria-hidden="true">
              <path d="M0 78 L38 52 L62 66 L96 30 L118 48 L142 14 L168 44 L196 24 L228 58 L254 40 L286 62 L312 46 L340 68" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
              <path d="M0 88 L52 68 L88 80 L128 52 L156 70 L192 42 L224 66 L262 50 L300 74 L340 58" fill="none" stroke="currentColor" stroke-width="1" stroke-linejoin="round" stroke-linecap="round" opacity="0.5" />
            </svg>
          </aside>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { demoIntakeSteps } from '~/data/demo-intake-steps';
import { featuredAppStoreReviews } from '~/data/customer-stories';
import { countryCodeOptions } from '~/data/phone-country-codes';
import { buildBookingSource } from '~/utils/tracking/bookingSource';
import {
  createLeadOnce,
  demoLeadAnswers,
  formatDemoSlot,
  supportsCalendarFirst,
  type DemoSlot,
} from '~/utils/tracking/demoCalendarFirst';

type DemoTriggerElement = HTMLAnchorElement & {
  dataset: DOMStringMap & {
    conversionLabel?: string;
    conversionLocation?: string;
  };
};

const config = useRuntimeConfig();
const { current: currentAttributionState, leadTrackingFields } = useAttribution();
const { sessionTouch, sessionTouchField, leadUtm } = useSessionTouch();
const { adConsent } = useCookieConsent();
const { track } = useMetaPixel();
const route = useRoute();

const steps = demoIntakeSteps;
const proofReviews = featuredAppStoreReviews;

const isOpen = ref(false);
const stageIndex = ref(0);
const answers = reactive<Record<string, string>>({});
const contact = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
  botField: '',
});
const fullName = computed(() => `${contact.firstName} ${contact.lastName}`.trim());
// Bound to the ISO code, not the dial code: several countries share +1, so a
// dial-keyed selection could not tell them apart.
const countryIso = ref('US');
const countryDial = computed(
  () => countryCodeOptions.find((country) => country.iso === countryIso.value)?.dial || '+1',
);
const submitting = ref(false);
const submitted = ref(false);
const submitError = ref('');
const fieldErrors = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
});
const conversionLabel = ref('Request demo');
const conversionLocation = ref('');
const sourceHref = ref('');
const dialogPanel = ref<HTMLElement | null>(null);
const dialogLayer = ref<HTMLElement | null>(null);
const bookingSlot = ref<HTMLElement | null>(null);
const bookingUnavailable = ref(false);
const triggerElement = ref<HTMLElement | null>(null);
let previousBodyOverflow = '';
let advanceTimer: ReturnType<typeof setTimeout> | undefined;

// 'calendar': time → details → confirm (booking.js reports the hand-off).
// 'legacy': today's questions → contact → calendar, for an older booking.js.
// 'request': calendar visitor with no time that works; questions → contact → thanks.
type DemoFlow = 'calendar' | 'legacy' | 'request';
const flow = ref<DemoFlow>('legacy');
const calendarStage = ref<'time' | 'details' | 'confirm'>('time');
const chosenSlot = ref<DemoSlot | null>(null);
const calendarStages = ['time', 'details', 'confirm'] as const;

const currentStep = computed(() => steps[stageIndex.value] || null);
const totalSteps = computed(() =>
  flow.value === 'calendar' ? calendarStages.length : flow.value === 'request' ? steps.length + 1 : steps.length + 2,
);
const progressIndex = computed(() =>
  flow.value === 'calendar' ? calendarStages.indexOf(calendarStage.value) : stageIndex.value,
);
const showsCalendar = computed(() => flow.value === 'calendar' && calendarStage.value !== 'details');
const showsControls = computed(() =>
  flow.value === 'calendar' ? calendarStage.value === 'details' : !submitted.value,
);
const chosenSlotLabel = computed(() =>
  chosenSlot.value ? formatDemoSlot(chosenSlot.value, document.documentElement.lang || 'en-US') : '',
);

// On iOS the layout viewport does not shrink when the keyboard opens, so a
// fixed inset-0 layer leaves the sheet behind the keyboard. Pin the layer to
// the visual viewport on phones and coarse-pointer tablets.
const isMobileSheet = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;

const shouldTrackViewport = () =>
  typeof window !== 'undefined' &&
  (isMobileSheet() || window.matchMedia('(pointer: coarse)').matches);

const syncLayerViewport = () => {
  const layer = dialogLayer.value;
  if (!layer || !isOpen.value) return;
  if (!shouldTrackViewport() || !window.visualViewport) {
    layer.style.height = '';
    layer.style.transform = '';
    return;
  }
  const vv = window.visualViewport;
  layer.style.height = `${vv.height}px`;
  layer.style.transform = `translateY(${vv.offsetTop}px)`;
};

const resetLayerViewport = () => {
  if (!dialogLayer.value) return;
  dialogLayer.value.style.height = '';
  dialogLayer.value.style.transform = '';
};

const openDialog = async (element: DemoTriggerElement) => {
  triggerElement.value = element;
  sourceHref.value = element.href || '';
  conversionLabel.value = element.dataset.conversionLabel || element.textContent?.trim() || 'Request demo';
  conversionLocation.value = element.dataset.conversionLocation || route.path;
  destroyBooking();
  stageIndex.value = 0;
  // A fresh dialog is a fresh lead; within it, the lead posts at most once.
  lead = createLeadOnce(sendLead);
  flow.value = supportsCalendarFirst((window as unknown as { W369Booking?: unknown }).W369Booking) ? 'calendar' : 'legacy';
  calendarStage.value = 'time';
  chosenSlot.value = null;
  Object.keys(answers).forEach((key) => delete answers[key]);
  contact.firstName = '';
  contact.lastName = '';
  contact.email = '';
  contact.phone = '';
  countryIso.value = 'US';
  contact.message = '';
  contact.botField = '';
  submitError.value = '';
  fieldErrors.firstName = '';
  fieldErrors.lastName = '';
  fieldErrors.email = '';
  fieldErrors.phone = '';
  submitted.value = false;
  isOpen.value = true;
  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  await nextTick();
  syncLayerViewport();
  focusTitle();
  if (flow.value === 'calendar') void mountBooking('pick');
};

// Each step replaces the content the visitor was on (an answer auto-advances), so focus moves to
// the new step's title; otherwise it falls to <body> and a screen reader never hears the question.
const focusTitle = () => {
  nextTick(() => dialogPanel.value?.querySelector<HTMLElement>('#demo-dialog-title')?.focus({ preventScroll: true }));
};
watch([stageIndex, calendarStage, flow], () => {
  if (isOpen.value) focusTitle();
});

const closeDialog = () => {
  if (submitting.value) return;
  destroyBooking();
  isOpen.value = false;
  resetLayerViewport();
  document.body.style.overflow = previousBodyOverflow;
  nextTick(() => triggerElement.value?.focus());
};

// Runs in the capture phase: NuxtLink navigates from its own click handler, and
// vue-router only skips that when the event is already defaultPrevented. A
// bubble-phase listener would fire too late and the page would route away.
const handleDocumentClick = (event: MouseEvent) => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target instanceof Element ? event.target : null;
  const element = target?.closest<HTMLElement>('[data-conversion-event="demo_request_click"]');
  if (!element) return;
  // The attribute marks the CTA as a demo request, whatever it is built from —
  // anchor to /contact-sales, mailto fallback, or a plain button.
  event.preventDefault();
  event.stopPropagation();
  void openDialog(element as DemoTriggerElement);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeDialog();
    return;
  }
  if (event.key !== 'Tab' || !dialogPanel.value) return;
  const focusable = Array.from(
    dialogPanel.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ),
  );
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

function selectOption(id: string, value: string) {
  answers[id] = value;
  window.clearTimeout(advanceTimer);
  advanceTimer = window.setTimeout(() => {
    if (stageIndex.value < steps.length) stageIndex.value += 1;
  }, 140);
}

function goBack() {
  if (flow.value === 'calendar') {
    changeTime();
    return;
  }
  window.clearTimeout(advanceTimer);
  stageIndex.value = Math.max(0, stageIndex.value - 1);
  submitError.value = '';
}

const emitCapturedEvent = () => {
  const payload = {
    event_category: 'conversion',
    event_label: conversionLabel.value,
    location: conversionLocation.value,
    page_path: window.location.pathname,
  };

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'demo_lead_captured', payload);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'demo_lead_captured', ...payload });
};

type BookingApi = {
  mount: (target: HTMLElement, options: Record<string, unknown>) => { destroy?: () => void } | null;
  capabilities?: readonly string[];
};

let bookingHandle: { destroy?: () => void } | null = null;
let bookingWatchdog: ReturnType<typeof setTimeout> | undefined;

const destroyBooking = () => {
  window.clearTimeout(bookingWatchdog);
  bookingUnavailable.value = false;
  try {
    bookingHandle?.destroy?.();
  } catch {
    // scheduler already gone
  }
  bookingHandle = null;
};

const w369Ready = (fn: (api: BookingApi) => void) => {
  const api = (window as unknown as { W369Booking?: BookingApi }).W369Booking;
  if (api && typeof api.mount === 'function') {
    fn(api);
    return;
  }
  window.addEventListener(
    'w369:ready',
    (event) => fn((event as CustomEvent<{ api: BookingApi }>).detail.api),
    { once: true },
  );
};

const optionLabel = (stepId: string, value: string) => {
  const step = steps.find((item) => item.id === stepId);
  return step?.options.find((option) => option.value === value)?.label || value || '';
};

const bookingSource = () =>
  buildBookingSource(
    adConsent.value,
    sessionTouch.value,
    adConsent.value === 'granted' ? currentAttributionState() : null,
  );

const bookingPrefill = () => ({
  name: fullName.value,
  email: contact.email,
  phone: contact.phone,
  notes: contact.message,
  metadata: {
    priority: optionLabel('priority', answers.priority),
    team_size: optionLabel('teamSize', answers.teamSize),
    timing: optionLabel('timing', answers.timing),
    sales_help: optionLabel('salesHelp', answers.salesHelp),
    source: 'workspace369.com/demo',
  },
});

// 'pick': the calendar hands the time back (w369:slot). 'confirm': the same
// calendar remounted on that time, rechecked, with the details prefilled and
// this site's own Terms and Privacy on its consent line. 'legacy': today's.
const bookingOptions = (mode: 'pick' | 'confirm' | 'legacy'): Record<string, unknown> => {
  if (mode === 'pick') return { layout: 'minimal', accent: '#1ba7ce', handoff: 'time', source: bookingSource() };
  const options: Record<string, unknown> = {
    layout: 'minimal',
    accent: '#1ba7ce',
    step: 'time',
    prefill: bookingPrefill(),
    // Where this lead came from, stored on the booking and its new client on 369.dev
    // (utils/tracking/bookingSource.ts, consent-gated like the lead itself).
    source: bookingSource(),
  };
  if (mode === 'confirm') {
    options.slot = chosenSlot.value?.startMs;
    options.legal = {
      terms: `${window.location.origin}/terms-of-service/`,
      privacy: `${window.location.origin}/privacy-policy/`,
    };
  }
  return options;
};

const mountBooking = async (mode: 'pick' | 'confirm' | 'legacy' = 'legacy') => {
  await nextTick();
  const slot = bookingSlot.value;
  if (!slot) return;
  destroyBooking();
  // The scheduler renders its own host element into the slot. If nothing lands
  // there — script blocked, API down, mount threw — surface the support route.
  bookingUnavailable.value = false;
  window.clearTimeout(bookingWatchdog);
  bookingWatchdog = window.setTimeout(() => {
    bookingUnavailable.value = !bookingSlot.value?.childElementCount;
  }, 8000);
  w369Ready((api) => {
    bookingHandle = api.mount(slot, bookingOptions(mode));
  });
};

// The calendar's hand-off: keep the time, move to the details. Only from our
// own pick-stage mount; nothing is posted or tracked here.
const handleSlot = (event: Event) => {
  const detail = (event as CustomEvent<{ startMs?: number; endMs?: number; container?: Node }>).detail || {};
  if (flow.value !== 'calendar' || calendarStage.value !== 'time') return;
  if (!detail.container || !bookingSlot.value?.contains(detail.container)) return;
  if (!Number.isFinite(detail.startMs) || !Number.isFinite(detail.endMs)) return;
  chosenSlot.value = { startMs: detail.startMs as number, endMs: detail.endMs as number };
  destroyBooking();
  calendarStage.value = 'details';
  submitError.value = '';
};

function changeTime() {
  calendarStage.value = 'time';
  submitError.value = '';
  void mountBooking('pick');
}

function startRequestOnly() {
  destroyBooking();
  flow.value = 'request';
  stageIndex.value = 0;
}

const handleBooked = (event: Event) => {
  const detail = (event as CustomEvent<{ bookingId?: string; status?: string; startMs?: number }>).detail || {};
  track('Schedule', { content_name: 'demo' });
  const payload = {
    event_category: 'conversion',
    event_label: conversionLabel.value,
    location: conversionLocation.value,
    booking_id: detail.bookingId,
    status: detail.status,
    start_ms: detail.startMs,
    page_path: window.location.pathname,
  };

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'demo_booked', payload);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'demo_booked', ...payload });
};

async function submitForm() {
  if (submitting.value) return;

  fieldErrors.firstName = contact.firstName ? '' : 'Add your first name.';
  fieldErrors.lastName = contact.lastName ? '' : 'Add your last name.';
  fieldErrors.email = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contact.email)
    ? ''
    : 'Enter a valid work email address.';
  // Normalise the phone to E.164 so the scheduler can skip its details step:
  // an entry that already starts with "+" is taken as-is, otherwise the picked
  // country code is prefixed to the national number (leading zeros dropped).
  const typedPhone = contact.phone.replace(/[\s()-]/g, '');
  const normalisedPhone = !typedPhone
    ? ''
    : typedPhone.startsWith('+')
      ? typedPhone
      : `${countryDial.value}${typedPhone.replace(/\D/g, '').replace(/^0+/, '')}`;
  fieldErrors.phone =
    !normalisedPhone || /^\+[1-9]\d{7,14}$/.test(normalisedPhone)
      ? ''
      : 'Enter a valid phone number, e.g. 415 555 0100.';

  if (fieldErrors.firstName || fieldErrors.lastName || fieldErrors.email || fieldErrors.phone) {
    await nextTick();
    dialogPanel.value?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    return;
  }

  contact.phone = normalisedPhone;

  submitting.value = true;
  submitError.value = '';

  const referralId = (() => {
    try {
      return new URL(sourceHref.value, window.location.origin).searchParams.get('ref') || '';
    } catch {
      return '';
    }
  })();

  const payload = {
    form_name: 'workspace369-demo-modal',
    flow: 'Sales request',
    answers:
      flow.value === 'calendar'
        ? demoLeadAnswers({ ...answers }, chosenSlot.value, Intl.DateTimeFormat().resolvedOptions().timeZone || '')
        : { ...answers },
    fullName: fullName.value,
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email,
    phone: contact.phone,
    message: contact.message,
    bot_field: contact.botField,
    source_path: `${window.location.pathname}${window.location.search}`.slice(0, 500),
    locale: document.documentElement.lang || 'en',
    conversion_label: conversionLabel.value,
    conversion_location: conversionLocation.value,
    referral_id: referralId,
    ...leadUtm(),
    ...sessionTouchField(),
    ...leadTrackingFields(),
    submittedAt: new Date().toISOString(),
  };

  pendingPayload = payload;
  try {
    const result = await lead.run();
    if (result.status === 'failed') throw result.error;
    if (result.status === 'busy') return;
    // The conversion fires only for the post that captured the lead: a visitor
    // who changed the time after it ('already') moves on without a second one.
    if (result.status === 'posted') {
      emitCapturedEvent();
      if (result.body?.event_id) track('Lead', { content_name: 'demo' }, result.body?.event_id);
    }
    if (flow.value === 'calendar') {
      calendarStage.value = 'confirm';
      void mountBooking('confirm');
      return;
    }
    submitted.value = true;
    stageIndex.value = steps.length + 1;
    if (flow.value === 'legacy') void mountBooking('legacy');
  } catch (error) {
    console.error('Workspace369 demo request submission failed', error);
    submitError.value =
      'We could not submit that request. Please try again, or email support@workspace369.com.';
  } finally {
    submitting.value = false;
  }
}

let pendingPayload: Record<string, unknown> | null = null;

async function sendLead(): Promise<{ event_id?: string } | null> {
  const response = await fetch(config.public.contactLeadEndpoint as string, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(pendingPayload),
  });
  if (!response.ok) throw new Error(`Submission failed with ${response.status}`);
  return (await response.json().catch(() => ({}))) as { event_id?: string } | null;
}

let lead = createLeadOnce(sendLead);

onMounted(() => {
  document.addEventListener('click', handleDocumentClick, true);
  document.addEventListener('keydown', handleKeydown);
  window.addEventListener('w369:booked', handleBooked);
  window.addEventListener('w369:slot', handleSlot);
  window.visualViewport?.addEventListener('resize', syncLayerViewport);
  window.visualViewport?.addEventListener('scroll', syncLayerViewport);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick, true);
  document.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('w369:booked', handleBooked);
  window.removeEventListener('w369:slot', handleSlot);
  window.visualViewport?.removeEventListener('resize', syncLayerViewport);
  window.visualViewport?.removeEventListener('scroll', syncLayerViewport);
  window.clearTimeout(advanceTimer);
  window.clearTimeout(bookingWatchdog);
  destroyBooking();
  document.body.style.overflow = previousBodyOverflow;
});
</script>

<style scoped>
.demo-dialog-layer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgb(2 6 23 / 74%);
  backdrop-filter: blur(14px);
}

.demo-dialog-panel {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(340px, 0.85fr);
  width: min(980px, 100%);
  /* Fixed, not max-height: steps have very different content heights, and a
     content-sized panel moved its own edges on every step (328px of travel on
     mobile, where the sheet is bottom-anchored). The controls pin to the bottom
     via margin-top:auto, so short steps just carry more breathing room. */
  height: min(720px, calc(100% - 48px));
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid rgb(203 213 225 / 80%);
  border-radius: 12px;
  background: #fff;
  color: #020617;
  box-shadow: 0 32px 100px rgb(2 6 23 / 42%);
}

.dark .demo-dialog-panel {
  border-color: rgb(51 65 85);
  background: #0f172a;
  color: white;
}

.demo-dialog-panel-booking {
  grid-template-columns: minmax(0, 1fr);
}

.demo-dialog-panel-booking .demo-dialog-proof {
  display: none;
}

/* When the form spans the whole panel (booking step, and phones/tablets where the reviews column is
   hidden), the progress row runs under the close button: keep its label and last bar clear of it. */
.demo-dialog-panel-booking .demo-dialog-progress {
  padding-inline-end: 44px;
}

@media (max-width: 900px) {
  .demo-dialog-progress {
    padding-inline-end: 44px;
  }
}

.demo-dialog-close {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 2;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  background: #fff;
  color: #334155;
  font-size: 25px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 150ms ease, color 150ms ease, transform 150ms ease;
}

.demo-dialog-close:hover {
  border-color: #0891b2;
  color: #0e7490;
  transform: rotate(4deg);
}

.dark .demo-dialog-close {
  border-color: #334155;
  background: #020617;
  color: #cbd5e1;
}

.demo-dialog-main {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 1.75rem 1.75rem 1.5rem;
}

.demo-dialog-progress-copy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 750;
}

.demo-dialog-progress-copy span {
  color: #0e8eb3;
  text-transform: uppercase;
}

.demo-dialog-progress-copy strong {
  color: #475569;
  font-size: inherit;
}

.dark .demo-dialog-progress-copy strong {
  color: #94a3b8;
}

.demo-dialog-progress-track {
  display: flex;
  gap: 0.3rem;
  margin-top: 0.65rem;
}

.demo-dialog-progress-track span {
  flex: 1 1 0;
  height: 0.2rem;
  background: #e2e8f0;
  transition: background 180ms ease;
}

.dark .demo-dialog-progress-track span {
  background: #334155;
}

.demo-dialog-progress-track span.complete {
  background: #1ba7ce;
}

.demo-dialog-step {
  min-width: 0;
  margin: 1.75rem 0 0;
  border: 0;
  padding: 0;
}

.demo-dialog-step legend,
.demo-dialog-booking-step h2 {
  max-width: 34rem;
  color: inherit;
  font-size: clamp(1.5rem, 3.2vw, 2.05rem);
  font-weight: 700;
  line-height: 1.15;
}

.demo-dialog-step > p,
.demo-dialog-booking-step > p {
  max-width: 34rem;
  margin: 0.7rem 0 0;
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.65;
}

.dark .demo-dialog-step > p,
.dark .demo-dialog-booking-step > p {
  color: #94a3b8;
}

.demo-dialog-options {
  display: grid;
  gap: 0.55rem;
  margin-top: 1.35rem;
}

.demo-dialog-option {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.85rem;
  align-items: center;
  width: 100%;
  min-height: 3.5rem;
  border: 1px solid #dbe3ed;
  border-radius: 0.5rem;
  background: transparent;
  color: inherit;
  padding: 0.7rem 0.85rem;
  text-align: left;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, transform 150ms ease;
}

.dark .demo-dialog-option {
  border-color: #334155;
}

.demo-dialog-option.selected {
  border-color: rgb(27 167 206 / 0.7);
  background: rgb(27 167 206 / 0.07);
  transform: translateX(0.15rem);
}

/* Hover only where a pointer really hovers: on a phone, :hover sticks to the
   last tap, and the next step renders the option under that spot looking
   already picked. */
@media (hover: hover) {
  .demo-dialog-option:hover {
    border-color: rgb(27 167 206 / 0.7);
    background: rgb(27 167 206 / 0.07);
    transform: translateX(0.15rem);
  }
}

.demo-dialog-option:focus-visible,
.demo-dialog-back:focus-visible,
.demo-dialog-submit:focus-visible,
.demo-dialog-done:focus-visible,
.demo-dialog-fields input:focus-visible,
.demo-dialog-fields select:focus-visible,
.demo-dialog-fields textarea:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

.demo-dialog-option-copy {
  display: grid;
  gap: 0.18rem;
  min-width: 0;
}

.demo-dialog-option-copy strong {
  font-size: 0.88rem;
  font-weight: 750;
}

.demo-dialog-option-copy small {
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.45;
}

.dark .demo-dialog-option-copy small {
  color: #94a3b8;
}

.demo-dialog-option-arrow {
  color: #1ba7ce;
  font-size: 1rem;
}

.demo-dialog-fields {
  display: grid;
  gap: 0.9rem;
  margin-top: 1.25rem;
}

.demo-dialog-fields label,
.demo-dialog-fields .demo-dialog-field {
  display: grid;
  gap: 0.4rem;
}

.demo-dialog-fields label > span {
  color: #334155;
  font-size: 0.76rem;
  font-weight: 750;
}

.dark .demo-dialog-fields label > span {
  color: #cbd5e1;
}

.demo-dialog-fields em {
  margin-left: 0.25rem;
  color: #94a3b8;
  font-size: 0.66rem;
  font-style: normal;
  font-weight: 550;
}

.demo-dialog-fields input,
.demo-dialog-fields select,
.demo-dialog-fields textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background-color: #f8fafc;
  color: #0f172a;
  padding: 0.7rem 0.8rem;
  font-size: 0.9rem;
}

.dark .demo-dialog-fields input,
.dark .demo-dialog-fields select,
.dark .demo-dialog-fields textarea {
  border-color: #334155;
  background-color: #020617;
  color: white;
}

.demo-dialog-fields textarea {
  resize: vertical;
}

.demo-dialog-name-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.75rem;
}

.demo-dialog-phone-row {
  display: grid;
  grid-template-columns: minmax(0, 7rem) minmax(0, 1fr);
  gap: 0.5rem;
}

@media (max-width: 420px) {
  .demo-dialog-name-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.demo-dialog-step > p.demo-dialog-terms {
  margin-top: 0.35rem;
  color: #334155;
  font-weight: 600;
}

.dark .demo-dialog-step > p.demo-dialog-terms {
  color: #cbd5e1;
}

/* Focus lands on the step title so screen readers read the new question; it is not a control. */
#demo-dialog-title:focus {
  outline: none;
}

.demo-dialog-privacy {
  font-size: 0.7rem !important;
  line-height: 1.5 !important;
}

.demo-dialog-privacy a {
  color: #0e8eb3;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.15rem;
}

.demo-dialog-honeypot {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.demo-dialog-controls {
  display: grid;
  grid-template-columns: auto minmax(0, auto);
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: auto;
  padding-top: 1.25rem;
}

.demo-dialog-back,
.demo-dialog-submit,
.demo-dialog-done {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.7rem;
  border-radius: 0.375rem;
  padding: 0 1rem;
  font-size: 0.78rem;
  font-weight: 750;
  cursor: pointer;
}

.demo-dialog-back {
  gap: 0.4rem;
  border: 0;
  background: transparent;
  color: #475569;
}

.dark .demo-dialog-back {
  color: #cbd5e1;
}

.demo-dialog-submit,
.demo-dialog-done {
  gap: 0.45rem;
  border: 1px solid #0e7490;
  background: #0e7490;
  color: white;
}

.demo-dialog-submit:hover,
.demo-dialog-done:hover {
  border-color: #155e75;
  background: #155e75;
}

.demo-dialog-submit:disabled {
  cursor: wait;
  opacity: 0.65;
}

.demo-dialog-error {
  margin-top: 0.8rem;
  color: #dc2626;
  font-size: 0.75rem;
  line-height: 1.5;
}

.demo-dialog-field-error {
  margin: 0;
  color: #dc2626;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.45;
}

.demo-dialog-fields input[aria-invalid='true'] {
  border-color: #dc2626;
}

.demo-dialog-booking-step {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.demo-dialog-booking-step h2 {
  margin-top: 0;
}

.demo-dialog-booking-slot {
  width: 100%;
  margin-top: 1.35rem;
}

.demo-dialog-booking-fallback {
  margin: 1.1rem 0 0 !important;
  color: #475569;
  font-size: 0.85rem;
  line-height: 1.6;
}

.dark .demo-dialog-booking-fallback {
  color: #cbd5e1;
}

.demo-dialog-booking-fallback a {
  color: #0e8eb3;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.15rem;
}

.demo-dialog-booking-slot p {
  margin: 0;
  color: #475569;
  font-size: 0.85rem;
  line-height: 1.6;
}

.dark .demo-dialog-booking-slot p {
  color: #cbd5e1;
}

.demo-dialog-booking-slot a {
  color: #0e8eb3;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.15rem;
}

.demo-dialog-done {
  margin-top: 1.5rem;
}

.demo-dialog-proof {
  position: relative;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  padding: 2rem 1.6rem;
  background:
    radial-gradient(70% 105% at 14% 0%, rgb(125 211 252 / 0.4), transparent 62%),
    radial-gradient(60% 110% at 86% 8%, rgb(196 181 253 / 0.38), transparent 62%),
    radial-gradient(85% 130% at 50% 118%, rgb(249 168 212 / 0.3), transparent 66%),
    linear-gradient(115deg, #f0f9fe, #f5f4ff 50%, #fdf4f9);
}

.demo-dialog-proof-quote {
  position: absolute;
  top: 1.4rem;
  right: 1.4rem;
  width: 3rem;
  height: auto;
  color: rgb(14 116 144 / 0.14);
}

.demo-dialog-proof-ridge {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 5.6rem;
  color: rgb(14 116 144 / 0.22);
  pointer-events: none;
}

.demo-dialog-proof-kicker {
  color: #0e7490;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.demo-dialog-proof h3 {
  margin-top: 0.6rem;
  color: #0f172a;
  font-size: 1.25rem;
  font-weight: 750;
  line-height: 1.25;
}

.demo-dialog-proof ul {
  display: grid;
  gap: 1.35rem;
  margin-top: 1.5rem;
}

.demo-dialog-proof li {
  display: grid;
  gap: 0.35rem;
}

.demo-dialog-stars {
  color: #0e7490;
  font-size: 0.7rem;
  letter-spacing: 0.12em;
}

.demo-dialog-proof blockquote {
  color: #1e293b;
  font-size: 0.82rem;
  line-height: 1.55;
}

.demo-dialog-proof li > p {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 650;
}

.demo-dialog-proof-foot {
  position: relative;
  z-index: 1;
  margin-top: auto;
  padding-top: 1.5rem;
  padding-bottom: 1.75rem;
  color: #0e7490;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.5;
}

.demo-dialog-proof-foot-trial {
  margin-top: 0;
  padding-top: 0;
}

.demo-dialog-enter-active,
.demo-dialog-leave-active {
  transition: opacity 180ms ease;
}

.demo-dialog-enter-from,
.demo-dialog-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .demo-dialog-panel {
    grid-template-columns: minmax(0, 1fr);
  }

  .demo-dialog-proof {
    display: none;
  }
}

@media (max-width: 760px) {
  .demo-dialog-layer {
    align-items: end;
    padding: 0;
  }

  .demo-dialog-panel {
    width: 100%;
    height: calc(100% - 24px);
    border-radius: 14px 14px 0 0;
  }

  .demo-dialog-main {
    padding: 1.35rem 1.15rem 1.25rem;
  }

  .demo-dialog-progress-copy {
    padding-right: 3rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .demo-dialog-option,
  .demo-dialog-progress-track span {
    transition-duration: 1ms;
  }
}
/* Calendar-first flow */
.demo-dialog-booking-lede {
  margin: 0.5rem 0 0 !important;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.55;
}

.dark .demo-dialog-booking-lede {
  color: #cbd5e1;
}

.demo-dialog-request-only {
  margin-top: 1.1rem;
  padding: 0.35rem 0;
  border: 0;
  background: none;
  color: #0e8eb3;
  font-size: 0.88rem;
  font-weight: 700;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 0.15rem;
  cursor: pointer;
}

.demo-dialog-slot-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.65rem 0.8rem;
  border: 1px solid rgb(27 167 206 / 0.45);
  border-radius: 0.5rem;
  background: rgb(27 167 206 / 0.07);
  font-size: 0.88rem;
}

.demo-dialog-slot-chip button {
  flex: none;
  min-height: 2.25rem;
  padding: 0 0.5rem;
  border: 0;
  background: none;
  color: #0e8eb3;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.15rem;
  cursor: pointer;
}

.demo-dialog-request-only:focus-visible,
.demo-dialog-slot-chip button:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

.demo-dialog-prepare {
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.dark .demo-dialog-prepare {
  border-top-color: #334155;
}

.demo-dialog-prepare-title {
  margin: 0 !important;
  color: #0f172a;
  font-size: 0.85rem !important;
  font-weight: 750;
}

.dark .demo-dialog-prepare-title {
  color: white;
}

.demo-dialog-prepare-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.75rem;
}

@media (max-width: 420px) {
  .demo-dialog-prepare-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
